/*
 * File: DriveHistory.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import type { ReactNode } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';

export interface DriveHistoryProps<T> {
  /**
   * Title displayed above the summary stats inside the accordion header.
   * Defaults to "Drive History".
   */
  title?: string;

  /**
   * A React element that displays the summary counts for the drive history.
   * Typically a <DriveHistorySummaryStats /> component.
   */
  summaryStats: ReactNode;

  /**
   * The array of drive history items to be displayed.
   * These are passed to the renderItem prop to produce visual list rows.
   */
  items: T[];

  /**
   * Render function used to convert each item into a list row component.
   * @example
   * renderItem={(item) => <DriveHistoryItem key={item.id} {...item} />}
   */
  renderItem: (item: T) => ReactNode;

  /**
   * Optional message displayed when no drive history items exist.
   * Defaults to "No previous drives".
   */
  emptyMessage?: string;
}

/**
 * Accordion wrapper component for displaying a student's drive history.
 *
 * This component abstracts the layout structure of:
 * - A titled accordion header
 * - Summary statistic row (always shown)
 * - A collapsible list of drive entries
 *
 * @template T - The shape of each drive item passed in via the `items` array.
 *
 * @example
 * <DriveHistory
 *   summaryStats={
 *     <DriveHistorySummaryStats
 *       completedCount={stats.completed}
 *       canceledCount={stats.canceled}
 *     />
 *   }
 *   items={driveHistoryItems}
 *   renderItem={(item) => (
 *     <DriveHistoryItem key={item.id} {...item} />
 *   )}
 * />
 */
export default function DriveHistory<T>({
  title = 'Drive History',
  summaryStats,
  items,
  renderItem,
  emptyMessage = 'No previous drives',
}: DriveHistoryProps<T>) {
  const hasContent = items.length > 0;

  return (
    <Accordion defaultExpanded={false} elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 0,
          minHeight: 'auto',
          '&.Mui-expanded': { minHeight: 'auto' },
          '& .MuiAccordionSummary-content': { my: 1 },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            {title}
          </Typography>

          {summaryStats}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        {hasContent ? (
          <List dense disablePadding>
            {items.map((item) => renderItem(item))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {emptyMessage}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
